import { Col, DatePicker, Form, Input, Modal, Radio, Row, Select, Typography, Card } from "antd";
import dayjs from "dayjs";
import PropTypes from 'prop-types';
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { conducteurQualiteOptions, OPTIONS } from "./utils";
import { updateSinistre } from "../../../../../../hooks/useHooks";
import {
    EditOutlined, AlertOutlined, TeamOutlined, IdcardOutlined, EnvironmentOutlined,
} from "@ant-design/icons";
import axiosInstance from "../../../../../../variables/api/axiosInstance";
import { rightsService } from "../../../../../../services/rightsService";

const { Text } = Typography;

// En-tête de section : pastille colorée + titre, plus moderne qu'un simple Divider.
const SectionTitle = ({ icon, color = "#1890ff", children }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "2px 0 16px" }}>
        <span style={{
            width: 30, height: 30, borderRadius: 9, background: `${color}18`, color,
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
        }}>{icon}</span>
        <Text strong style={{ fontSize: 14.5, color: "#242166" }}>{children}</Text>
    </div>
);

SectionTitle.propTypes = { icon: PropTypes.node, color: PropTypes.string, children: PropTypes.node };

const sectionCardStyle = { borderRadius: 14, border: "1px solid #eef0f4", marginBottom: 16 };

export const EditModal = ({ visible, onCancel, sinistre, loading, returnPath = "/details-sinistre/v2" }) => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const [entitesConstat, setEntitesConstat] = useState([]);
    const [saving, setSaving] = useState(false);
    // Ré-édition de la date de déclaration réservée à l'habilitation HSI100. Sans le droit, le
    // champ est masqué et le backend conserve la valeur existante (payload forgé neutralisé).
    const peutEditerDateDeclaration = rightsService("HSI100");

    // Réactivité des sections conditionnelles (sinon elles ne s'affichent pas au clic).
    const temoignage = Form.useWatch("temoignage", form);
    const conduisait = Form.useWatch("conduisait", form);

    // Chargement de la liste des entités de constat (absente auparavant → select vide).
    useEffect(() => {
        if (!visible) return;
        axiosInstance
            .get("/api/entite-constat")
            .then((res) => setEntitesConstat(Array.isArray(res.data) ? res.data : []))
            .catch((err) => console.log(err));
    }, [visible]);

    useEffect(() => {
        if (sinistre) {
            form.setFieldsValue({
                dateSinistre: sinistre.dateSinistre ? dayjs(sinistre.dateSinistre) : null,
                dateDeclaration: sinistre.dateDeclaration ? dayjs(sinistre.dateDeclaration) : null,
                lieu: sinistre.lieu || '',
                entiteConstat: sinistre.entiteConstat?.entiteConstatId ?? null,
                circonstance: sinistre.circonstance || '',
                provenanceAssure: sinistre.provenanceAssure || '',
                destinationAssure: sinistre.destinationAssure || '',
                vitesseAssure: sinistre.vitesseAssure || '',
                observationAgent: sinistre.observationAgent || '',
                typeDommageAssure: sinistre.typeDommageAssure || null,
                temoignage: sinistre.temoin ? 'oui' : 'non',
                nomTemoin: sinistre.temoin?.nom || '',
                prenomTemoin: sinistre.temoin?.prenom || '',
                telephoneTemoin: sinistre.temoin?.contact?.[0] || sinistre.temoin?.telephone || '',
                adresseTemoin: sinistre.temoin?.adresse || '',
                conduisait: sinistre.conducteur ? 'non' : 'oui',
                numeroPermis: sinistre.numeroPermis || '',
                categoriePermis: sinistre.categoriePermis || null,
                dateDelivrancePermis: sinistre.dateDelivrancePermis ? dayjs(sinistre.dateDelivrancePermis) : null,
                nomConducteur: sinistre.conducteur?.nom || '',
                prenomConducteur: sinistre.conducteur?.prenom || '',
                telephoneConducteur: sinistre.conducteur?.contact?.[0] || sinistre.conducteur?.telephone || '',
            });
        }
    }, [sinistre, form]);

    const handleOk = () => {
        form.validateFields().then(async (values) => {
            const entiteObj = entitesConstat.find((e) => e.entiteConstatId === values.entiteConstat) || null;

            // PUT /api/sinistre écrase TOUS les champs (SinistreCopier.copyForUpdate) → on
            // repart de l'objet complet et on n'écrase que les champs édités ici. temoin /
            // conducteur (Personne @ManyToOne) sont conservés tels quels : ce endpoint ne met
            // pas à jour leurs sous-champs.
            const payload = {
                ...sinistre,
                dateSinistre: values.dateSinistre
                    ? values.dateSinistre.format("YYYY-MM-DD[T]HH:mm:ss")
                    : sinistre.dateSinistre,
                // Date de déclaration : envoyée seulement si l'utilisateur est habilité (HSI100).
                // Sinon on renvoie la valeur existante — le backend la conserve de toute façon.
                dateDeclaration: (peutEditerDateDeclaration && values.dateDeclaration)
                    ? values.dateDeclaration.format("YYYY-MM-DD[T]HH:mm:ss")
                    : sinistre.dateDeclaration,
                lieu: values.lieu || null,
                entiteConstat: entiteObj,
                circonstance: values.circonstance || null,
                provenanceAssure: values.provenanceAssure || null,
                destinationAssure: values.destinationAssure || null,
                vitesseAssure:
                    values.vitesseAssure !== "" && values.vitesseAssure != null
                        ? Number(values.vitesseAssure)
                        : null,
                observationAgent: values.observationAgent || null,
                typeDommageAssure: values.typeDommageAssure || null,
            };

            try {
                setSaving(true);
                const response = await updateSinistre(payload);
                const effectiveCodeId = response?.data?.codeId || sinistre?.codeId;
                toast.success("Informations du sinistre mises à jour avec succès.");
                onCancel();
                // La mise à jour crée un nouvel enregistrement (historique) avec un nouveau
                // codeId → on recharge le détail sur ce codeId (même logique que ChangeTypeModal).
                navigate(returnPath, { state: { codeId: effectiveCodeId }, replace: true });
            } catch (error) {
                console.error(error);
                toast.error("Erreur lors de la mise à jour des informations du sinistre.");
            } finally {
                setSaving(false);
            }
        }).catch(() => { });
    };

    const entiteOptions = entitesConstat.map((e) => ({ label: e.nom, value: e.entiteConstatId }));

    return (
        <Modal
            title={
                <div>
                    <Text strong style={{ fontSize: 17, color: "#242166" }}>
                        <EditOutlined style={{ marginRight: 8 }} /> Modifier les informations du sinistre
                    </Text>
                    {sinistre?.numSinistre && (
                        <div><Text type="secondary" style={{ fontSize: 12 }}>Dossier N° {sinistre.numSinistre}</Text></div>
                    )}
                </div>
            }
            open={visible}
            onCancel={onCancel}
            confirmLoading={saving || loading}
            onOk={handleOk}
            okText="Enregistrer"
            cancelText="Annuler"
            width={1000}
            centered
            styles={{ body: { maxHeight: "72vh", overflowY: "auto", paddingRight: 8 } }}
        >
            <Form layout="vertical" form={form} initialValues={{ temoignage: 'non', conduisait: 'oui' }}>

                <Card size="small" style={sectionCardStyle} bodyStyle={{ padding: 18 }}>
                    <SectionTitle icon={<AlertOutlined />} color="#ff4d4f">Informations sur l'accident</SectionTitle>
                    <Row gutter={16}>
                        <Col xs={24} md={12} lg={8}>
                            <Form.Item name="dateSinistre" label="Date de survenance"
                                rules={[{ required: true, message: 'Sélectionnez la date du sinistre' }]}>
                                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                            </Form.Item>
                        </Col>
                        {/* Date de déclaration : ré-éditable UNIQUEMENT sous habilitation HSI100.
                            Mêmes contrôles qu'à la déclaration (pas de futur, >= survenance). */}
                        {peutEditerDateDeclaration && (
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item
                                    name="dateDeclaration"
                                    label="Date de déclaration"
                                    dependencies={['dateSinistre']}
                                    rules={[
                                        {
                                            validator: (_, value) => {
                                                if (!value) return Promise.resolve();
                                                if (value.startOf('day').isAfter(dayjs().startOf('day'))) {
                                                    return Promise.reject('La date de déclaration ne peut pas être dans le futur');
                                                }
                                                const survenance = form.getFieldValue('dateSinistre');
                                                if (survenance && value.startOf('day').isBefore(dayjs(survenance).startOf('day'))) {
                                                    return Promise.reject('La déclaration ne peut pas précéder la survenance');
                                                }
                                                return Promise.resolve();
                                            }
                                        }
                                    ]}
                                >
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        format="YYYY-MM-DD"
                                        disabledDate={(c) => c && c > new Date()}
                                    />
                                </Form.Item>
                            </Col>
                        )}
                        <Col xs={24} md={12} lg={8}>
                            <Form.Item name="lieu" label="Lieu de survenance">
                                <Input prefix={<EnvironmentOutlined style={{ color: "#bfbfbf" }} />} placeholder="Lieu du sinistre" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12} lg={8}>
                            <Form.Item name="entiteConstat" label="Entité de constat">
                                <Select showSearch allowClear placeholder="Sélectionnez l'entité"
                                    optionFilterProp="label" options={entiteOptions} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24} md={12} lg={6}>
                            <Form.Item name="provenanceAssure" label="Provenance">
                                <Input placeholder="Provenance de l'assuré" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12} lg={6}>
                            <Form.Item name="destinationAssure" label="Destination">
                                <Input placeholder="Destination de l'assuré" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12} lg={6}>
                            <Form.Item name="vitesseAssure" label="Vitesse (km/h)">
                                <Input type="number" placeholder="Vitesse" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12} lg={6}>
                            <Form.Item name="typeDommageAssure" label="Type de dommage">
                                <Select placeholder="Sélectionnez" allowClear options={OPTIONS.typeDommages} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item name="circonstance" label="Circonstances">
                        <Input.TextArea rows={4} placeholder="Décrivez les circonstances de l'accident" />
                    </Form.Item>
                    <Form.Item name="observationAgent" label="Observation de l'agent" style={{ marginBottom: 0 }}>
                        <Input.TextArea rows={3} placeholder="Observations complémentaires" />
                    </Form.Item>
                </Card>

                <Card size="small" style={sectionCardStyle} bodyStyle={{ padding: 18 }}>
                    <SectionTitle icon={<TeamOutlined />} color="#722ed1">Témoignage</SectionTitle>
                    <Form.Item name="temoignage" label="Y a-t-il un témoin ?" style={{ marginBottom: temoignage === "oui" ? 16 : 0 }}>
                        <Radio.Group optionType="button" buttonStyle="solid">
                            <Radio.Button value="non">Non</Radio.Button>
                            <Radio.Button value="oui">Oui</Radio.Button>
                        </Radio.Group>
                    </Form.Item>
                    {temoignage === "oui" && (
                        <>
                            <Row gutter={16}>
                                <Col xs={24} md={12} lg={8}>
                                    <Form.Item name="nomTemoin" label="Nom du témoin"><Input placeholder="Nom du témoin" /></Form.Item>
                                </Col>
                                <Col xs={24} md={12} lg={8}>
                                    <Form.Item name="prenomTemoin" label="Prénom(s) du témoin"><Input placeholder="Prénom du témoin" /></Form.Item>
                                </Col>
                                <Col xs={24} md={12} lg={8}>
                                    <Form.Item name="telephoneTemoin" label="Téléphone du témoin"><Input placeholder="Téléphone" /></Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item name="adresseTemoin" label="Adresse du témoin" style={{ marginBottom: 0 }}><Input placeholder="Adresse du témoin" /></Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item name="fonctionTemoin" label="Qualité / Fonction du témoin" style={{ marginBottom: 0 }}><Input placeholder="Qualité / Fonction" /></Form.Item>
                                </Col>
                            </Row>
                        </>
                    )}
                </Card>

                <Card size="small" style={{ ...sectionCardStyle, marginBottom: 0 }} bodyStyle={{ padding: 18 }}>
                    <SectionTitle icon={<IdcardOutlined />} color="#1890ff">Informations du conducteur</SectionTitle>
                    <Form.Item name="conduisait" label="L'assuré conduisait-il ?" style={{ marginBottom: 16 }}>
                        <Radio.Group optionType="button" buttonStyle="solid">
                            <Radio.Button value="oui">Oui</Radio.Button>
                            <Radio.Button value="non">Non</Radio.Button>
                        </Radio.Group>
                    </Form.Item>
                    <Row gutter={16}>
                        <Col xs={24} md={12} lg={8}>
                            <Form.Item name="numeroPermis" label="Numéro du permis"><Input placeholder="Numéro de permis" /></Form.Item>
                        </Col>
                        <Col xs={24} md={12} lg={8}>
                            <Form.Item name="categoriePermis" label="Catégorie du permis">
                                <Select placeholder="Sélectionnez" options={OPTIONS.permisCategories} allowClear />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12} lg={8}>
                            <Form.Item name="dateDelivrancePermis" label="Date de délivrance">
                                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                            </Form.Item>
                        </Col>
                    </Row>
                    {conduisait === "non" && (
                        <Row gutter={16} style={{ marginTop: 4 }}>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name="nomConducteur" label="Nom du conducteur" style={{ marginBottom: 0 }}><Input placeholder="Nom du conducteur" /></Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name="prenomConducteur" label="Prénom(s) du conducteur" style={{ marginBottom: 0 }}><Input placeholder="Prénom du conducteur" /></Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name="telephoneConducteur" label="Téléphone du conducteur" style={{ marginBottom: 0 }}><Input placeholder="Téléphone" /></Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8} style={{ marginTop: 16 }}>
                                <Form.Item name="conducteurQualite" label="Qualité du conducteur" style={{ marginBottom: 0 }}>
                                    <Select placeholder="Sélectionnez" allowClear options={conducteurQualiteOptions} />
                                </Form.Item>
                            </Col>
                        </Row>
                    )}
                </Card>
            </Form>
        </Modal>
    );
};

EditModal.propTypes = {
    visible: PropTypes.bool,
    onCancel: PropTypes.func,
    sinistre: PropTypes.object,
    loading: PropTypes.bool
};
